import "./src/styles/global.scss"
import "./src/styles/font-faces.scss"

import { RouteUpdateArgs } from "gatsby"

import { SEGMENT_LOADED_EVENT_ID } from "./src/shared-components/seo/use-segment-is-loaded"
import { initWebVitals } from "./src/utils/analytics/web-vitals"
import { ensureTrailingSlash } from "./src/utils/anchor-links"
import { QUERY_PARAM_CHANGE_EVENT_ID } from "./src/utils/contexts/page-context"
import {
  getSessionLocale,
  setSessionLocale,
} from "./src/utils/local-storage-user-info"
import {
  getLocaleFromPathname,
  getLocalizedPagePath,
  isAlternativeLocale,
} from "./src/utils/locales"

export { wrapPageElement } from "./gatsby-shared"

export function onRouteUpdate({ location, prevLocation }: RouteUpdateArgs) {
  if (location.search !== prevLocation?.search) {
    const event = new CustomEvent(QUERY_PARAM_CHANGE_EVENT_ID, {
      detail: {
        pathname: location.pathname,
        search: location.search,
      },
    })
    document.dispatchEvent(event)
  }
}

export function onClientEntry() {
  // given non-English session
  // upgrade to localized path when available
  const sessionLocale = getSessionLocale()

  if (!isAlternativeLocale(sessionLocale)) return

  const { pathname } = window.location
  const pageLocale = getLocaleFromPathname(pathname)
  const currentPageMatchesAlternativeSessionLocale =
    pageLocale === sessionLocale

  // we're already on a localized page, so no path change necessary
  // NOTE(localization): need to modify behavior here once we add French
  if (currentPageMatchesAlternativeSessionLocale) return

  const path = ensureTrailingSlash(pathname)
  const localizedPagePath = getLocalizedPagePath(path, sessionLocale)

  if (localizedPagePath) {
    // Preserve query parameters during redirect
    const url = new URL(window.location.href)
    url.pathname = localizedPagePath

    // Use native browser redirect as Gatsby navigate might not be ready yet in onClientEntry
    window.location.replace(url.href)
  }
}

export function onInitialClientRender() {
  const pageLocale = getLocaleFromPathname(window.location.pathname)

  // save non-English locale as user preference in local storage
  if (isAlternativeLocale(pageLocale)) {
    setSessionLocale(pageLocale)
  }

  if (window.analytics) {
    initWebVitals()
    return
  }

  const handleSegmentLoaded = () => {
    initWebVitals()
    document.removeEventListener(SEGMENT_LOADED_EVENT_ID, handleSegmentLoaded)
  }

  document.addEventListener(SEGMENT_LOADED_EVENT_ID, handleSegmentLoaded)
}
